import React from 'react'
import {useTranslation} from 'react-i18next'
import {PaymentMethodRowProps} from './types'
import {PAYMENT_METHODS} from './paymentMethodRegistry'

/**
 * Renders a single payment method as a flat selectable row.
 * Uses SVG components from registry as icons (fallback from API icon_url).
 */
export function PaymentMethodRow({
  method,
  isSelected,
  onSelect,
}: PaymentMethodRowProps) {
  const {t} = useTranslation()
  // Look up icon + subtitle renderer from the static registry
  const registryEntry = PAYMENT_METHODS.find((m) => m.id === method.method_id)

  const renderIcon = () => {
    if (!registryEntry) {
      // No registry entry — try icon_url from API
      return method.icon_url ? <img src={method.icon_url} className='w-40px me-3' alt='' /> : null
    }
    const icon = registryEntry.icon
    if (typeof icon === 'object' && 'type' in icon && icon.type === 'image') {
      return <img src={icon.src} className={icon.className || 'w-40px me-3'} alt='' />
    }
    const IconComponent = icon as React.ComponentType<React.SVGProps<SVGSVGElement> & {className?: string}>
    return <IconComponent className='w-40px me-3' />
  }

  const renderSubtitle = () => {
    // Use registry's subtitleRenderer if available (e.g., Visa+MC icons for credit card)
    if (registryEntry?.subtitleRenderer) {
      return registryEntry.subtitleRenderer()
    }
    if (method.subtitle_icons) {
      return (
        <div className='d-flex gap-2 align-items-center'>
          {method.subtitle_icons.map((iconUrl, i) => (
            <img key={i} src={iconUrl} className='h-20px' alt='' />
          ))}
        </div>
      )
    }
    // i18n: per-method subtitle key, falling back to the DB string if no translation exists
    // so any method without a key (or a new one) still renders.
    return method.subtitle
      ? t(`PAYMENT_SUBTITLE_${method.method_id}`, {defaultValue: method.subtitle})
      : method.subtitle
  }

  return (
    <>
      <div className='py-1'>
        <div
          className='py-3 d-flex flex-stack flex-nowrap'
          style={{cursor: 'pointer'}}
          onClick={onSelect}
        >
          <div className='d-flex align-items-center flex-grow-1 min-w-0'>
            {renderIcon()}

            <div className='me-3'>
              <div className='d-flex align-items-center fw-bolder'>
                {t(`PAYMENT_LABEL_${method.method_id}`, {defaultValue: method.label})}
              </div>
              <div className='text-muted'>
                {renderSubtitle()}
              </div>
            </div>
          </div>

          <div className='d-flex align-items-center ms-2 ms-md-9 flex-shrink-0'>
            <label className='form-check form-check-custom form-check-solid'>
              <input
                className='form-check-input'
                type='radio'
                name='payment_method'
                checked={isSelected}
                onChange={onSelect}
                onClick={(e) => e.stopPropagation()}
              />
            </label>
          </div>
        </div>
      </div>
      <div className='separator separator-dashed'></div>
    </>
  )
}
